import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { imgUrl } from "../../api";
import "./FotosOrdenables.css";

// Evita que tocar un botón dentro de la foto empiece a arrastrarla
const noArrastrar = {
  onMouseDown: (e) => e.stopPropagation(),
  onTouchStart: (e) => e.stopPropagation(),
  onKeyDown: (e) => e.stopPropagation(),
};

function FotoItem({ img, index, total, deshabilitado, onPortada, onEliminar }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: img.id, disabled: deshabilitado });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const esPortada = index === 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`fo-item ${esPortada ? "fo-portada" : ""} ${isDragging ? "fo-arrastrando" : ""}`}
      aria-label={`Foto ${index + 1} de ${total}${esPortada ? ", portada" : ""}. Arrastra para cambiar el orden`}
      {...attributes}
      {...listeners}
    >
      <img src={imgUrl(img.url_thumb || img.url)} alt={img.alt_text || `Foto ${index + 1}`} draggable={false} />

      <span className="fo-num">{index + 1}</span>
      {esPortada && <span className="fo-badge">PORTADA</span>}

      <div className="fo-acciones">
        <button
          type="button"
          className="fo-btn fo-btn-del"
          title="Eliminar foto"
          aria-label="Eliminar foto"
          disabled={deshabilitado}
          onClick={() => onEliminar(img.id)}
          {...noArrastrar}
        >
          🗑️
        </button>
      </div>
    </div>
  );
}

/**
 * Cuadrícula de fotos que se reordena arrastrando (mouse, dedo o teclado).
 * - onReordenar(nuevaLista): recibe las fotos ya en el nuevo orden.
 * - onEliminar(id)
 */
export default function FotosOrdenables({ imagenes, onReordenar, onEliminar, deshabilitado = false }) {
  const sensors = useSensors(
    // Mouse: empieza a arrastrar después de mover 6px (así los clics siguen funcionando)
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // Dedo: mantener presionado 200ms (así se puede seguir haciendo scroll en la página)
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const desde = imagenes.findIndex((i) => i.id === active.id);
    const hacia = imagenes.findIndex((i) => i.id === over.id);
    if (desde < 0 || hacia < 0) return;
    onReordenar(arrayMove(imagenes, desde, hacia));
  };

  const ponerPortada = (imgId) => {
    const desde = imagenes.findIndex((i) => i.id === imgId);
    if (desde > 0) onReordenar(arrayMove(imagenes, desde, 0));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={imagenes.map((i) => i.id)} strategy={rectSortingStrategy}>
        <div className="fo-grid">
          {imagenes.map((img, index) => (
            <FotoItem
              key={img.id}
              img={img}
              index={index}
              total={imagenes.length}
              deshabilitado={deshabilitado}
              onPortada={ponerPortada}
              onEliminar={onEliminar}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
